'use client';

import { useEffect, useState } from 'react';

import { SILENCE_NUDGE_MS, WAVE_BAR_COUNT } from '../constants';

type AudioLevel = {
	/** One 0..1 value per wave bar, tallest in the middle */
	bars: number[];
	/** True once nothing has risen above the noise gate for SILENCE_NUDGE_MS */
	isSilent: boolean;
};

const QUIET_BARS: number[] = Array(WAVE_BAR_COUNT).fill(0);
const IDLE_LEVEL: AudioLevel = { bars: QUIET_BARS, isSilent: false };

// Loudness (dBFS) that fills the bars; normal speech at laptop distance peaks around here
const LOUD_DB = -22;
// Anything quieter than this is silence, however quiet the room is
const ABSOLUTE_GATE_DB = -68;
// Sound must rise this far above the room's background noise to move the bars
const NOISE_GATE_DB = 9;
// Background noise estimate creeps up this fast (dB per second) and drops instantly,
// so steady hum/fan noise settles into the floor while speech pauses keep it low
const NOISE_FLOOR_RISE_DB_PER_SEC = 1.5;
// Below this is digital silence (e.g. the zeroed frames before the mic delivers audio), not room
// noise. Letting it set the floor would leave the floor ~100 dB too low for a minute.
const DIGITAL_SILENCE_DB = -100;
// < 1 lifts quiet sounds so soft voices still move the bars visibly
const LEVEL_CURVE = 0.8;
// Per-frame (60 fps) smoothing: bars rise fast and fall back gradually, like a real meter
const ATTACK = 0.55;
const RELEASE = 0.8;
const FRAME_MS = 1000 / 60;
// Wave shape at full volume: tallest in the middle, tapering to the edges
const BAR_SHAPE = [0.6, 1, 1, 0.6].slice(0, WAVE_BAR_COUNT);
// Each bar sways slightly out of step with its neighbours so the wave doesn't move as one block
const SWAY = 0.2;
const SWAY_SPEED = 1 / 110;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * Live mic levels of a stream's audio track, read via Web Audio.
 * Meters the same track MediaRecorder records, so flat bars mean a silent recording.
 */
export function useAudioLevel(stream: MediaStream | null): AudioLevel {
	const [audioLevel, setAudioLevel] = useState<AudioLevel>(IDLE_LEVEL);

	useEffect(() => {
		const audioTrack = stream?.getAudioTracks()[0];
		if (!audioTrack || typeof AudioContext === 'undefined') return;

		const context = new AudioContext();
		// Safari can start the context suspended when it isn't created inside a user gesture
		context.resume().catch(() => {});

		const source = context.createMediaStreamSource(new MediaStream([audioTrack]));
		const analyser = context.createAnalyser();
		analyser.fftSize = 512;
		// Not connected to destination: connecting would play the mic back through the speakers
		source.connect(analyser);

		const timeSamples = new Float32Array(analyser.fftSize);
		const barLevels = Array(BAR_SHAPE.length).fill(0);
		let noiseFloorDb = Infinity;
		let lastFrameAt = performance.now();
		let lastLoudAt = lastFrameAt;
		let lastKey = '';
		let frameId = 0;

		const tick = () => {
			const now = performance.now();
			// Scale smoothing by real frame time so 120 Hz screens animate at the same speed
			const frames = Math.min(4, (now - lastFrameAt) / FRAME_MS);
			lastFrameAt = now;

			analyser.getFloatTimeDomainData(timeSamples);
			let sumSquares = 0;
			for (const sample of timeSamples) sumSquares += sample * sample;
			const rms = Math.sqrt(sumSquares / timeSamples.length);

			// Loudness above the room's background noise drives both the wave and the silence nudge
			const db = 20 * Math.log10(rms + 1e-9);
			if (db > DIGITAL_SILENCE_DB) {
				noiseFloorDb =
					db < noiseFloorDb
						? db
						: noiseFloorDb + (NOISE_FLOOR_RISE_DB_PER_SEC * frames * FRAME_MS) / 1000;
			}
			// No real audio yet means no floor; fall back to the absolute gate
			const gateDb = Number.isFinite(noiseFloorDb)
				? Math.max(ABSOLUTE_GATE_DB, noiseFloorDb + NOISE_GATE_DB)
				: ABSOLUTE_GATE_DB;
			const loudness = Math.pow(clamp01((db - gateDb) / (LOUD_DB - gateDb)), LEVEL_CURVE);
			if (loudness > 0) lastLoudAt = now;
			const isSilent = now - lastLoudAt > SILENCE_NUDGE_MS;

			const attack = 1 - Math.pow(1 - ATTACK, frames);
			const release = Math.pow(RELEASE, frames);
			const bars = BAR_SHAPE.map((shape, index) => {
				const sway = 1 - SWAY + SWAY * Math.sin(now * SWAY_SPEED + index * 1.9);
				const target = loudness * shape * sway;
				const current = barLevels[index];
				barLevels[index] =
					target > current ? current + (target - current) * attack : current * release;
				return Math.round(barLevels[index] * 50) / 50;
			});

			// Only re-render when something visible changed
			const key = `${bars.join(',')}|${isSilent}`;
			if (key !== lastKey) {
				lastKey = key;
				setAudioLevel({ bars, isSilent });
			}
			frameId = requestAnimationFrame(tick);
		};
		frameId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(frameId);
			source.disconnect();
			context.close().catch(() => {});
			setAudioLevel(IDLE_LEVEL);
		};
	}, [stream]);

	return audioLevel;
}
